import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useCallback, useRef } from 'react';
import { useSocket } from './useSocket';
const SocketContext = createContext(undefined);
export function SocketProvider({ url, children }) {
    // Store reconnect handlers in a ref to avoid re-renders
    const reconnectHandlersRef = useRef(new Set());
    // Called when socket reconnects - notify all registered handlers
    const handleReconnect = useCallback(() => {
        reconnectHandlersRef.current.forEach(handler => {
            try {
                handler();
            }
            catch (error) {
                console.error('[SocketProvider] Reconnect handler error:', error);
            }
        });
    }, []);
    // Allow child components to register their reconnect handlers
    const registerReconnectHandler = useCallback((handler) => {
        reconnectHandlersRef.current.add(handler);
        // Return unregister function
        return () => {
            reconnectHandlersRef.current.delete(handler);
        };
    }, []);
    const { socket, isConnected, authenticate } = useSocket(url, {
        onReconnect: handleReconnect,
    });
    const value = useMemo(() => ({
        socket,
        isConnected,
        authenticate,
        registerReconnectHandler,
    }), [socket, isConnected, authenticate, registerReconnectHandler]);
    return (_jsx(SocketContext.Provider, { value: value, children: children }));
}
export function useSocketContext() {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocketContext must be used within a SocketProvider');
    }
    return context;
}
